/**
 * AvailabilityExceptions (BON-241) — the "Specific dates" list: one-off
 * overrides on top of the weekly rhythm. The add form lives in
 * AvailabilityExceptionForm; this component owns the list + copy.
 */
import { SLOT_LABELS, type ExceptionEntry } from '../../../api/availability';

import { AvailabilityExceptionForm } from './AvailabilityExceptionForm';
import { formatExceptionDate } from './availabilityModel';

export interface AvailabilityExceptionsProps {
  exceptions: ExceptionEntry[];
  onAdd: (entry: ExceptionEntry) => void;
  onRemove: (date: string) => void;
  disabled?: boolean;
}

function ExceptionRow({
  entry,
  onRemove,
  disabled,
}: {
  entry: ExceptionEntry;
  onRemove: (date: string) => void;
  disabled: boolean;
}) {
  return (
    <li
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 10px',
        borderRadius: 8,
        border: '1px solid var(--nb-border)',
        background: 'var(--nb-surface)',
      }}
    >
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ fontSize: 12, fontWeight: 700 }}>
          {formatExceptionDate(entry.date)} ·{' '}
          {entry.state === 'off' ? 'Blocked off' : `Open · ${SLOT_LABELS[entry.state]}`}
        </span>
        {entry.note && (
          <span style={{ display: 'block', fontSize: 11, color: 'var(--nb-subtle)' }}>
            {entry.note}
          </span>
        )}
      </span>
      <button
        type="button"
        className="nb-btn nb-btn-ghost"
        disabled={disabled}
        aria-label={`Remove ${formatExceptionDate(entry.date)}`}
        onClick={() => onRemove(entry.date)}
      >
        ✕
      </button>
    </li>
  );
}

export function AvailabilityExceptions({
  exceptions,
  onAdd,
  onRemove,
  disabled = false,
}: AvailabilityExceptionsProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <p style={{ margin: 0, fontSize: 11.5, color: 'var(--nb-subtle)' }}>
        Overrides for one-off days — a vacation, or a night you can work outside your usual rhythm.
      </p>

      {exceptions.length > 0 && (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 6 }}>
          {exceptions.map((entry) => (
            <ExceptionRow key={entry.date} entry={entry} onRemove={onRemove} disabled={disabled} />
          ))}
        </ul>
      )}

      <AvailabilityExceptionForm
        takenDates={exceptions.map((e) => e.date)}
        onAdd={onAdd}
        disabled={disabled}
      />

      <p style={{ margin: 0, fontSize: 11, color: 'var(--nb-faint)' }}>
        Past dates can’t be changed.
      </p>
    </div>
  );
}
